import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";
import logoFreelaDash from "@/assets/logo-freeladash.png";

export default function PrivacyPage() {
  return (
    <>
      <SEOHead 
        title="Política de Privacidade"
        description="Política de privacidade do FreelaDash conforme LGPD. Saiba como tratamos e protegemos seus dados."
        canonical="/privacidade"
      />
      <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto flex h-14 items-center justify-between px-4 sm:h-16">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={logoFreelaDash} 
              alt="FreelaDash" 
              className="h-8 w-8 rounded-lg object-contain"
            />
            <span className="font-display text-xl font-bold tracking-wide text-foreground" style={{ WebkitTextStroke: '0.5px currentColor' }}>
              FREELADASH
            </span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 sm:py-12">
        <article className="mx-auto max-w-3xl">
          <h1 className="mb-2 text-3xl font-bold sm:text-4xl">POLÍTICA DE PRIVACIDADE</h1>
          <p className="mb-8 text-sm text-muted-foreground">Última atualização: 06 de fevereiro de 2026</p>

          <div className="prose prose-gray max-w-none space-y-6 text-foreground">
            <section>
              <h2 className="text-xl font-semibold text-foreground">1. Introdução</h2>
              <p className="text-muted-foreground leading-relaxed">
                O FreelaDash ("nós", "nosso" ou "Plataforma") está comprometido em proteger sua privacidade. 
                Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos suas 
                informações pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">2. Dados que Coletamos</h2>
              
              <h3 className="text-lg font-medium text-foreground mt-4">2.1 Dados fornecidos por você</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Informações de conta: nome, email (obtidos via Google OAuth)</li>
                <li>Foto de perfil (se disponibilizada pelo Google)</li>
                <li>Dados de projetos e tarefas que você cadastra</li>
                <li>Informações de clientes que você registra</li>
                <li>Registros de tempo e timesheet</li>
                <li>Dados financeiros básicos (valores de projetos, pagamentos)</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-4">2.2 Dados coletados automaticamente</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Endereço IP</li>
                <li>Tipo de navegador e dispositivo</li>
                <li>Páginas visitadas e tempo de uso</li>
                <li>Data e hora de acesso</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">3. Finalidade do Uso dos Dados</h2>
              <p className="text-muted-foreground leading-relaxed">Utilizamos seus dados para:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Fornecer e manter os serviços da plataforma</li>
                <li>Processar pagamentos e gerenciar assinaturas</li>
                <li>Enviar emails transacionais (confirmações, notificações importantes)</li>
                <li>Melhorar e personalizar sua experiência</li>
                <li>Fornecer suporte ao cliente</li>
                <li>Garantir a segurança da plataforma</li>
                <li>Cumprir obrigações legais</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">4. Base Legal para Tratamento</h2>
              <p className="text-muted-foreground leading-relaxed">
                Tratamos seus dados com base nas seguintes hipóteses legais da LGPD:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Execução de contrato:</strong> para fornecer os serviços contratados</li>
                <li><strong>Consentimento:</strong> quando você aceita nossos termos</li>
                <li><strong>Interesse legítimo:</strong> para melhorar nossos serviços e segurança</li>
                <li><strong>Cumprimento de obrigação legal:</strong> quando exigido por lei</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">5. Armazenamento e Segurança</h2>
              <p className="text-muted-foreground leading-relaxed">
                Seus dados são armazenados em servidores seguros com criptografia. Utilizamos medidas técnicas 
                e organizacionais para proteger suas informações contra acesso não autorizado, perda ou destruição, 
                incluindo:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Criptografia de dados em trânsito (HTTPS/TLS)</li>
                <li>Criptografia de dados em repouso</li>
                <li>Controle de acesso baseado em funções</li>
                <li>Monitoramento contínuo de segurança</li>
                <li>Backups regulares</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">6. Compartilhamento de Dados</h2>
              <p className="text-muted-foreground leading-relaxed">
                <strong>Não vendemos seus dados.</strong> Compartilhamos informações apenas com:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Stripe:</strong> para processamento de pagamentos</li>
                <li><strong>Resend:</strong> para envio de emails transacionais</li>
                <li><strong>Google:</strong> para autenticação via OAuth</li>
                <li><strong>Autoridades:</strong> quando exigido por lei ou ordem judicial</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-2">
                <strong>Não utilizamos seus dados para publicidade ou marketing de terceiros.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">7. Seus Direitos (LGPD)</h2>
              <p className="text-muted-foreground leading-relaxed">
                Conforme a LGPD, você tem direito a:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Acesso:</strong> saber quais dados temos sobre você</li>
                <li><strong>Correção:</strong> corrigir dados incompletos ou desatualizados</li>
                <li><strong>Exclusão:</strong> solicitar a eliminação dos seus dados</li>
                <li><strong>Portabilidade:</strong> receber seus dados em formato estruturado</li>
                <li><strong>Revogação:</strong> retirar seu consentimento a qualquer momento</li>
                <li><strong>Informação:</strong> saber com quem compartilhamos seus dados</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-2">
                Para exercer esses direitos, entre em contato pelo email{" "}
                <a href="mailto:jmkoygg@gmail.com" className="text-primary hover:underline">
                  jmkoygg@gmail.com
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">8. Exclusão de Conta e Dados</h2>
              <p className="text-muted-foreground leading-relaxed">
                Você pode solicitar a exclusão de sua conta a qualquer momento. Após a solicitação:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Seus dados pessoais serão excluídos em até 30 dias</li>
                <li>Dados necessários para cumprimento legal podem ser retidos pelo período exigido</li>
                <li>Backups são sobrescritos em até 90 dias</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">9. Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                Utilizamos cookies essenciais para o funcionamento da plataforma, incluindo:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Cookies de sessão para manter você logado</li>
                <li>Cookies de preferências para lembrar suas configurações</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-2">
                Não utilizamos cookies de rastreamento para publicidade.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">10. Retenção de Dados</h2>
              <p className="text-muted-foreground leading-relaxed">
                Mantemos seus dados enquanto sua conta estiver ativa. Após exclusão da conta, 
                seus dados são removidos conforme descrito na seção 8, exceto quando a retenção 
                for necessária para cumprimento de obrigações legais.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">11. Alterações nesta Política</h2>
              <p className="text-muted-foreground leading-relaxed">
                Podemos atualizar esta Política de Privacidade periodicamente. Alterações significativas 
                serão comunicadas por email ou através de aviso na plataforma. Recomendamos revisar esta 
                página regularmente.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">12. Contato e Encarregado (DPO)</h2>
              <p className="text-muted-foreground leading-relaxed">
                Para questões sobre privacidade ou para exercer seus direitos:
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Email:</strong>{" "}
                <a href="mailto:jmkoygg@gmail.com" className="text-primary hover:underline">
                  jmkoygg@gmail.com
                </a>
              </p>
              <p className="text-muted-foreground mt-2">
                <strong className="text-foreground">Website:</strong>{" "}
                <a href="https://freeladash.com.br" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  freeladash.com.br
                </a>
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t py-6 bg-card mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          © 2026 FreelaDash. Todos os direitos reservados.
        </div>
      </footer>
    </div>
    </>
  );
}
